import { useState } from 'react';
import { useFormik } from 'formik';
import { Box, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import LoadingButton from '@mui/lab/LoadingButton';
import { pushDL } from '@lendica/api';
import * as api from '../api';
import { useAppContainerContext } from './AppContainer';
import { MessageCard } from './MessageCard';
import { Heading } from './Typography';
import { ResendTimer } from '@lendica/components';

interface OneTimePasswordProps {
    phone: string;
    setTokens: (tokens: any) => void;
    onNavBack: () => void;
}

export const OneTimePassword = ({ phone, setTokens, onNavBack }: OneTimePasswordProps) => {
    const { setMsg } = useAppContainerContext();
    const [loading, setLoading] = useState(false);
    const formik = useFormik({
        initialValues: {
            otpCode: '',
        },
        validationSchema: api.otpSchema,
        onSubmit: async ({ otpCode }) => {
            try {
                setLoading(true);
                const tokens = await api.phoneWithOTP(phone, otpCode);

                // Track successful OTP verification
                pushDL('auth_step', {
                    step: 'otp_verify',
                    phone: phone,
                });

                setTokens(tokens);
            } catch (error: any) {
                if (error?.response && error?.response?.status === 400) {
                    setMsg('The one-time password is incorrect.');

                    // Track OTP verification error
                    pushDL('auth_step', {
                        step: 'otp_verify_error',
                        phone: phone,
                        error: 'invalid_otp_code',
                    });
                } else {
                    // Track other OTP errors
                    pushDL('auth_step', {
                        step: 'otp_verify_error',
                        phone: phone,
                        error: error?.message || 'otp_verify_failed',
                    });
                }
            } finally {
                setLoading(false);
            }
        },
    });

    const handleResend = async () => {
        try {
            setLoading(true);
            await api.phone(phone);

            // Track OTP resend
            pushDL('auth_step', {
                step: 'otp_resend',
                phone: phone,
            });

            setMsg(
                `One-time code sent to ${phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}.`
            );
            setTimeout(() => {
                setMsg(null);
            }, 2500);
        } catch (error: any) {
            console.log(error);
            setMsg('Something went wrong while sending the OTC. Please try again later.');

            // Track OTP resend error
            pushDL('auth_step', {
                step: 'otp_resend_error',
                phone: phone,
                error: error?.message || 'otp_resend_failed',
            });
            setTimeout(() => {
                setMsg(null);
            }, 2500);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                pb: 4,
                flex: 1,
                gap: 2,
            }}
        >
            <IconButton
                size="small"
                onClick={() => {
                    // Track back navigation
                    pushDL('auth_step', {
                        step: 'otp_back_to_phone',
                        phone: phone,
                    });
                    onNavBack();
                }}
                sx={{ ml: -0.5, mb: 1 }}
            >
                <ArrowBackIosIcon />
            </IconButton>
            <Heading>Verify to get started</Heading>
            <MessageCard>Enter the 4-digit code we just sent to your phone.</MessageCard>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    height: 'fit-content',
                    mb: 3,
                    gap: 0,
                }}
            >
                <TextField
                    label="One-time code"
                    name="otpCode"
                    fullWidth
                    inputProps={{ 'maxLength': 4, 'data-testid': 'otp' }}
                    value={formik.values.otpCode}
                    onChange={formik.handleChange}
                    error={!!(formik.touched.otpCode && formik.errors.otpCode)}
                    helperText={
                        formik.touched.otpCode && formik.errors.otpCode
                            ? formik.errors.otpCode
                            : ' '
                    }
                    autoComplete="one-time-code"
                />
                <ResendTimer seconds={30} onResend={handleResend}>
                    Resend the code
                </ResendTimer>
            </Box>
            <LoadingButton
                type="submit"
                size="large"
                color="primary"
                variant="contained"
                data-testid="submit"
                fullWidth
                loading={loading}
            >
                Next
            </LoadingButton>
        </Box>
    );
};

export default OneTimePassword;
